import _ from 'lodash';
import React from 'react';

import {connect} from 'react-redux';

import Button from 'components/Button';
import TreeSelect from 'components/Filters/TreeSelect';
import SelectFilter from 'components/Filters/SelectFilter';
import ToggleSelectFilter from 'components/Filters/ToggleSelectFilter';
import TccwFloorFilter from 'components/Filters/TccwFloorFilter';
import AllocationTypeFilter from 'components/Filters/AllocationTypeFilter';
import RoomPurposeTypeWithDateRangeFilter from 'components/Filters/RoomPurposeTypeWithDateRangeFilter';

import ToggleFilter from 'components/Filters/ToggleFilter';

import {
  exportXlsx,
  exportXlsxInBackground,
  selectXlsxExporting
} from 'ducks/files';
import {requiresMultiplePermissions, PERMISSIONS} from 'ducks/auth';

import {
  StatusWithDateRangeFilter,
  OutOfOrderReasonWithDateRangeFilter
} from './components';

import {exportRoomAllocations as exportRoomAllocationsSdk} from 'sdk/allocations';
import {
  CLEANING_STATUSES,
  ROOM_OCCUPANCY_STATUSES,
  buildOfflinePeriodReasonOption
} from 'ducks/allocations';

import './styles.css';

const ROOM_ALLOCATIONS_EXPORT_FILENAME = 'room_allocations';

interface AllocationFiltersProps {
  locationRef: string;
  roomTypeFilters: Array<any>;
  filters: any;
  rooms: Array<any>;
  clearStatus: any;
  floors: Array<any>;
  requiresMultiplePermissions: any;
  exportXlsxInBackground: any;
  outOfOrderFilters: Array<any>;
  setFilter: (field: string, value: any, withoutFetching?: boolean) => void;
  clearFilters: () => void;
}

class AllocationsFilters extends React.Component<AllocationFiltersProps> {
  onStatusChange = option => {
    if (_.isNull(option)) {
      this.props.clearStatus();
    } else {
      this.props.setFilter('status', option);
    }
  };

  clearFilters = () => {
    this.props.clearFilters();
  };

  exportRoomAllocationsXlsx = () => {
    const {locationRef, filters} = this.props;

    const exportData = {
      locationRef,
      filters
    };

    this.props.requiresMultiplePermissions(
      {
        permissions: [
          {
            locationRef,
            permission: PERMISSIONS.VIEW_ALLOCATIONS
          },
          {
            permission: PERMISSIONS.VIEW_EXCEL_EXPORT_FILE
          }
        ]
      },
      () =>
        this.props.exportXlsxInBackground({
          exportData,
          exportSdk: exportRoomAllocationsSdk,
          fileName: ROOM_ALLOCATIONS_EXPORT_FILENAME
        })
    );
  };

  render() {
    const {
      rooms,
      roomTypeFilters,
      floors,
      filters,
      locationRef,
      setFilter,
      outOfOrderFilters
    } = this.props;

    const roomTypeData = roomTypeFilters.map(roomTypeGroup => ({
      label: roomTypeGroup.name,
      data: roomTypeGroup.roomTypes.map(roomType => ({
        label: roomType.name,
        key: roomType.ref
      }))
    }));
    const roomsOptions = rooms.map(room => ({
      label: room.roomNumber,
      value: room.id
    }));

    const floorOptions = floors.map(floor => ({label: floor, value: floor}));
    const outOfOrderOptions = outOfOrderFilters.map(
      buildOfflinePeriodReasonOption
    );

    return (
      <div className="AllocationsFilters colab-row-space-between colab-sidepadding-2">
        <div>
          <TreeSelect
            label="Room Type"
            classNameForTesting="roomType"
            data={roomTypeData}
            value={filters.roomTypes}
            onFilterChange={value =>
              setFilter('roomTypes', value.filter(x => x !== ''))
            }
          />
          <SelectFilter
            label="Room"
            classNameForTesting="room"
            data={roomsOptions}
            value={filters.rooms}
            onChange={value => setFilter('rooms', value.filter(x => x !== ''))}
          />
          {locationRef !== 'tccw' && (
            <SelectFilter
              label="Floor"
              classNameForTesting="floor"
              data={floorOptions}
              value={filters.floors.join(',')}
              onChange={value =>
                setFilter('floors', value.filter(x => x !== ''))
              }
            />
          )}
          {locationRef === 'tccw' && (
            <TccwFloorFilter
              onChange={value =>
                setFilter('floors', value.filter(x => x !== ''))
              }
              filters={filters}
              floors={floors}
            />
          )}
          <StatusWithDateRangeFilter
            data={ROOM_OCCUPANCY_STATUSES}
            classNameForTesting="roomOccupancyStatuses"
            statusValue={filters.status}
            onStatusChange={this.onStatusChange}
            start={filters.startDate}
            end={filters.endDate}
            onDateChange={(field, value) => setFilter(field, value)}
          />
          <ToggleSelectFilter
            label="Room Housekeeping statuses"
            classNameForTesting="roomHousekeepingStatuses"
            data={CLEANING_STATUSES}
            value={filters.roomCleaningStatuses}
            onChange={roomCleaningStatuses =>
              setFilter('roomCleaningStatuses', roomCleaningStatuses)
            }
          />
          <AllocationTypeFilter
            value={filters.dummy}
            classNameForTesting="allocationTypeFilter"
            onChange={value => setFilter('dummy', value)}
          />
          <OutOfOrderReasonWithDateRangeFilter
            data={outOfOrderOptions}
            outOfOrderReasons={filters.outOfOrderReasons}
            outOfOrderStartDate={filters.outOfOrderStartDate}
            outOfOrderEndDate={filters.outOfOrderEndDate}
            setFilter={this.props.setFilter}
          />
          <RoomPurposeTypeWithDateRangeFilter
            roomPurposeTypes={filters.roomPurposeTypes}
            purposeStartDate={filters.purposeStartDate}
            purposeEndDate={filters.purposeEndDate}
            setFilter={this.props.setFilter}
          />
          <ToggleFilter
            label="Archived Rooms"
            classNameForTesting="archivedRooms"
            value={filters.archived}
            onChange={() => setFilter('archived', !filters.archived)}
          />
        </div>
        <div>
          <Button
            white
            className="exportButton"
            onClick={() => this.exportRoomAllocationsXlsx()}>
            Export Room Allocations
          </Button>
          <Button clear onClick={this.clearFilters}>
            Clear all filters
          </Button>
        </div>
      </div>
    );
  }
}

const mapStateToProps = state => ({
  isExporting: selectXlsxExporting(state, ROOM_ALLOCATIONS_EXPORT_FILENAME)
});

const mapDispatchToProps = {
  exportXlsxInBackground,
  exportXlsx,
  requiresMultiplePermissions
};

export default connect(mapStateToProps, mapDispatchToProps)(AllocationsFilters);
